// (c) chaehongee. Confidential and proprietary.

import { useCallback, useState } from 'react'
import type { ReactElement } from 'react'
import type { LucideIcon } from 'lucide-react'
import {
    ArrowUpFromDot,
    ChevronLeft,
    ChevronRight,
    AlertCircle,
    Sparkles,
    Wand2,
    Video,
    Camera,
    ImageIcon,
    Focus,
} from 'lucide-react'

import styles from './PresetPage.module.css'
import softskinImg from './resource/softskin.webp'
import realisticskinImg from './resource/realisticskin.webp'
import imperfectskinImg from './resource/imperfectskin.webp'
import CameraOrbitPicker, { getAngleLabel } from './CameraOrbitPicker'
import { useAppDispatch, useAppSelector } from '../../../store/hooks/hooks'
import { useReadOnlyMode } from '../../../store/hooks/useReadOnlyMode'
import { useBackstageNav } from '../navigator/Router'
import { setInput } from '../../../store/slices/chatSlice'
import { selectActiveSessionId, selectActiveSession } from '../../../store/selectors/sessionSelectors'
import { useChatActions } from '../hooks/useChatActions'
import { selectActiveSelectedNodeIds } from '../../../store/selectors/selectionSelectors'
import { selectIsActiveChatProcessing } from '../../../store/selectors/chatSelectors'

// ── Types ──

interface StepChoice {
    label: string
    value: string
    image?: string
}

interface StepDefinition {
    title: string
    type: 'select' | 'toggle' | 'camera-orbit'
    key: string
    choices?: StepChoice[]
    defaultValue: string | boolean
}

interface PresetDefinition {
    id: string
    label: string
    description: string
    icon: LucideIcon
    placeholderImage?: string
    requiredImages: { min: number; max: number }
    /** If true, sends the prompt directly instead of pasting into textarea */
    sendImmediately?: boolean
    /** How many preview slots to show; defaults to requiredImages.max or 1 */
    previewSlots?: number | ((options: Record<string, string | boolean>) => number)
    steps: StepDefinition[]
    validateStep?: (
        stepIndex: number,
        options: Record<string, string | boolean>,
        nodeIds: string[]
    ) => string | null
    buildPrompt: (
        options: Record<string, string | boolean>,
        nodeIds: string[]
    ) => string
}

interface FlowState {
    presetId: string
    step: number
    options: Record<string, string | boolean>
    /** Camera orbit state for shot-angle preset */
    cameraAzimuth?: number
    cameraElevation?: number
    cameraZoom?: number
}

// ── Preset Definitions ──

export const PRESETS: PresetDefinition[] = [
    {
        id: 'topaz-upscale',
        label: 'Upscale',
        description: 'Topaz AI upscaling',
        icon: ArrowUpFromDot,
        requiredImages: { min: 1, max: 1 },
        steps: [
            {
                title: 'Enhancement Model',
                type: 'select',
                key: 'model',
                choices: [
                    { label: 'Standard V2', value: 'Standard V2' },
                    { label: 'Low Resolution V2', value: 'Low Resolution V2' },
                    { label: 'CGI', value: 'CGI' },
                    { label: 'High Fidelity V2', value: 'High Fidelity V2' },
                    { label: 'Text Refine', value: 'Text Refine' },
                ],
                defaultValue: '',
            },
            {
                title: 'Upscale Factor',
                type: 'select',
                key: 'factor',
                choices: [
                    { label: '2x', value: '2' },
                    { label: '4x', value: '4' },
                    { label: '6x', value: '6' },
                ],
                defaultValue: '',
            },
            {
                title: 'Face Enhancement',
                type: 'toggle',
                key: 'faceEnhance',
                defaultValue: false,
            },
        ],
        buildPrompt: (options) => {
            const parts = ['Upscale this image using Topaz']
            parts.push(`with the ${options.model} model`)
            parts.push(`at ${options.factor}x scale`)
            if (options.faceEnhance) parts.push('with face enhancement enabled')
            return parts.join(' ')
        },
    },
    {
        id: 'skin-texture',
        label: 'Skin Retouch',
        description: 'Beauty retouching',
        icon: Sparkles,
        requiredImages: { min: 1, max: 1 },
        steps: [
            {
                title: 'Retouch Style',
                type: 'select',
                key: 'style',
                choices: [
                    { label: 'Soft Skin', value: 'soft', image: softskinImg },
                    { label: 'Realistic Skin', value: 'realistic', image: realisticskinImg },
                    { label: 'Imperfect Skin', value: 'imperfect', image: imperfectskinImg },
                ],
                defaultValue: '',
            },
        ],
        buildPrompt: (options) => {
            if (options.style === 'soft') {
                return 'Edit this image with soft skin beauty retouching. ' +
                    'Smooth out blemishes and even out skin tone for a clean, polished editorial look. ' +
                    'Keep it natural but noticeably refined.'
            }
            if (options.style === 'imperfect') {
                return 'Edit this image with minimal skin retouching. ' +
                    'Preserve natural imperfections like freckles, pores, and fine lines. ' +
                    'Only remove temporary blemishes while keeping the skin looking authentic and unretouched.'
            }
            return 'Edit this image with realistic skin retouching. ' +
                'Smooth minor blemishes while preserving natural skin detail, pores, and texture. ' +
                'Keep the overall look realistic and not overly airbrushed.'
        },
    },
    {
        id: 'soul-generate',
        label: 'Soul Style',
        description: 'Higgsfield AI styles',
        icon: Wand2,
        requiredImages: { min: 0, max: 1 },
        steps: [
            {
                title: 'Style',
                type: 'select',
                key: 'style',
                choices: [
                    { label: 'Realistic', value: 'Realistic' },
                    { label: 'Y2K', value: 'Y2K' },
                    { label: 'Grunge', value: 'Grunge' },
                    { label: 'Anime', value: 'Anime' },
                    { label: 'Oil Painting', value: 'Oil Painting' },
                    { label: 'Watercolor', value: 'Watercolor' },
                    { label: 'Cyberpunk', value: 'Cyberpunk' },
                    { label: 'Pop Art', value: 'Pop Art' },
                    { label: 'Tokyo Streetstyle', value: 'Tokyo Streetstyle' },
                    { label: 'Overexposed', value: 'Overexposed' },
                    { label: 'iPhone', value: 'iPhone' },
                ],
                defaultValue: '',
            },
        ],
        buildPrompt: (options, nodeIds) => {
            if (nodeIds.length > 0) {
                return `Generate a ${options.style} style image using the selected image as reference`
            }
            return `Generate a ${options.style} style portrait`
        },
    },
    {
        id: 'kling-video',
        label: 'Video',
        description: 'Image-to-video',
        icon: Video,
        requiredImages: { min: 1, max: 2 },
        previewSlots: (options) => options.mode === 'start-end' ? 2 : 1,
        steps: [
            {
                title: 'Animation Mode',
                type: 'select',
                key: 'mode',
                choices: [
                    { label: 'Single Image', value: 'single' },
                    { label: 'Start & End Frame', value: 'start-end' },
                ],
                defaultValue: '',
            },
            {
                title: 'Duration',
                type: 'select',
                key: 'duration',
                choices: [
                    { label: '5 seconds', value: '5' },
                    { label: '10 seconds', value: '10' },
                ],
                defaultValue: '',
            },
        ],
        validateStep: (stepIndex, options, nodeIds) => {
            if (stepIndex === 0) {
                if (options.mode === 'start-end' && nodeIds.length < 2) {
                    return 'Select 2 images on the canvas for start & end frame mode'
                }
                if (nodeIds.length < 1) {
                    return 'Select at least 1 image on the canvas'
                }
            }
            return null
        },
        buildPrompt: (options) => {
            if (options.mode === 'single') {
                return `Generate a ${options.duration}-second video animation from this image`
            }
            return `Generate a ${options.duration}-second video interpolating between these two images`
        },
    },
    {
        id: 'multi-angle',
        label: 'Multi-Angle',
        description: 'Multiple camera angles',
        icon: Camera,
        requiredImages: { min: 1, max: 1 },
        steps: [
            {
                title: 'Number of Angles',
                type: 'select',
                key: 'count',
                choices: [
                    { label: '2', value: '2' },
                    { label: '3', value: '3' },
                    { label: '4', value: '4' },
                    { label: '6', value: '6' },
                ],
                defaultValue: '',
            },
            {
                title: 'Vary Poses',
                type: 'toggle',
                key: 'varyPoses',
                defaultValue: true,
            },
        ],
        buildPrompt: (options) => {
            const count = options.count
            const poses = options.varyPoses
            const parts = [
                `Generate ${count} parallel camera angles of the subject in this image.`,
                'Include a mix of front, side, three-quarter, and back views while maintaining consistent appearance.',
            ]
            if (poses) {
                parts.push('Vary the poses naturally across the angles.')
            } else {
                parts.push('Keep the same pose across all angles.')
            }
            return parts.join(' ')
        },
    },
    {
        id: 'shot-angle',
        label: 'Shot Angle',
        description: 'Change camera angle',
        icon: Focus,
        requiredImages: { min: 1, max: 1 },
        sendImmediately: true,
        steps: [
            {
                title: 'Camera Angle',
                type: 'camera-orbit',
                key: 'cameraAngle',
                defaultValue: '0,0', // azimuth,elevation
            },
        ],
        buildPrompt: (options) => {
            const [azStr, elStr] = String(options.cameraAngle).split(',')
            const rawAz = Number(azStr) || 0
            const rawEl = Number(elStr) || 0
            // Clamp to API-valid ranges and round to integers
            const az = Math.round(((rawAz % 360) + 360) % 360)
            const el = Math.round(Math.max(-30, Math.min(90, rawEl)))
            const zoom = Math.round(Math.max(0, Math.min(10, Number(options.cameraZoom) || 0)))
            const label = getAngleLabel(az, el)
            return `change the camera angle to ${label}\n<camera_angle_preset>\nhorizontal_angle=${az}\nvertical_angle=${el}\nzoom=${zoom}\n</camera_angle_preset>`
        },
    },
]

// ── Helpers ──

function buildDefaults(steps: StepDefinition[]): Record<string, string | boolean> {
    const defaults: Record<string, string | boolean> = {}
    for (const step of steps) {
        defaults[step.key] = step.defaultValue
    }
    return defaults
}

function getSlotCount(
    preset: PresetDefinition,
    options: Record<string, string | boolean>
): number {
    if (typeof preset.previewSlots === 'function') return preset.previewSlots(options)
    if (typeof preset.previewSlots === 'number') return preset.previewSlots
    return Math.max(preset.requiredImages.max, 1)
}

// ── Preview Slot Component ──

interface PreviewSlotProps {
    imageUrl?: string
    label: string
    tag?: string
}

function PreviewSlot({ imageUrl, label, tag }: PreviewSlotProps): ReactElement {
    return (
        <div className={styles.previewSlot} data-filled={imageUrl ? 'true' : undefined}>
            {imageUrl ? (
                <>
                    <img src={imageUrl} alt="" className={styles.previewSlotImage} />
                    {tag && <span className={styles.previewSlotTag}>{tag}</span>}
                </>
            ) : (
                <>
                    <ImageIcon className={styles.previewSlotIcon} />
                    <span className={styles.previewSlotLabel}>{label}</span>
                </>
            )}
        </div>
    )
}

// ── Main Component ──

export default function PresetPage(): ReactElement {
    const [flow, setFlow] = useState<FlowState | null>(null)
    const [tileError, setTileError] = useState<string | null>(null)
    const [stepError, setStepError] = useState<string | null>(null)

    const readOnly = useReadOnlyMode()
    const dispatch = useAppDispatch()
    const nav = useBackstageNav()
    const activeSessionId = useAppSelector(selectActiveSessionId)
    const session = useAppSelector(selectActiveSession)
    const selectedNodeIds = useAppSelector(selectActiveSelectedNodeIds)
    const isProcessing = useAppSelector(selectIsActiveChatProcessing)

    const { handleSendWithText } = useChatActions()

    const isDisabled = readOnly || isProcessing || !activeSessionId
    const nodes = session?.nodes || []

    // Resolve selected node image URLs
    const selectedImages = selectedNodeIds
        .map((id) => nodes.find((n) => n.id === id))
        .filter((n): n is NonNullable<typeof n> => n !== undefined)
        .map((n) => n.image_path)

    // Determine how many preview slots to show
    const activePreset = flow ? PRESETS.find((p) => p.id === flow.presetId) : null
    const flowOptions = flow?.options ?? {}
    const slotCount = activePreset
        ? getSlotCount(activePreset, flowOptions)
        : Math.max(selectedImages.length, 1)

    const slotLabels = activePreset?.id === 'kling-video' && flowOptions.mode === 'start-end'
        ? ['Start Frame', 'End Frame']
        : undefined

    const composeAndPaste = useCallback(
        (
            preset: PresetDefinition,
            options: Record<string, string | boolean>,
            nodeIds: string[]
        ) => {
            if (!activeSessionId) return
            const promptText = preset.buildPrompt(options, nodeIds)

            if (preset.sendImmediately) {
                void handleSendWithText(promptText)
            } else {
                dispatch(setInput({ sessionId: activeSessionId, input: promptText }))
                window.dispatchEvent(new CustomEvent('preset:prompt-ready'))
            }

            nav.replace('chat')
            setFlow(null)
        },
        [activeSessionId, dispatch, nav, handleSendWithText]
    )

    const handleTileClick = useCallback(
        (preset: PresetDefinition) => {
            if (isDisabled) return
            setTileError(null)

            const count = selectedNodeIds.length
            if (count < preset.requiredImages.min) {
                setTileError(
                    `Select ${preset.requiredImages.min} image${preset.requiredImages.min !== 1 ? 's' : ''} on the canvas first`
                )
                return
            }

            if (preset.steps.length === 0) {
                composeAndPaste(preset, {}, selectedNodeIds)
                return
            }

            setFlow({
                presetId: preset.id,
                step: 0,
                options: buildDefaults(preset.steps),
            })
        },
        [isDisabled, selectedNodeIds, composeAndPaste]
    )

    const advanceOrFinish = useCallback(
        (
            currentFlow: FlowState,
            updatedOptions: Record<string, string | boolean>
        ) => {
            const preset = PRESETS.find((p) => p.id === currentFlow.presetId)
            if (!preset) return

            if (preset.validateStep) {
                const error = preset.validateStep(
                    currentFlow.step,
                    updatedOptions,
                    selectedNodeIds
                )
                if (error) {
                    setStepError(error)
                    return
                }
            }

            const isLastStep = currentFlow.step === preset.steps.length - 1
            if (isLastStep) {
                composeAndPaste(preset, updatedOptions, selectedNodeIds)
            } else {
                setFlow({
                    ...currentFlow,
                    step: currentFlow.step + 1,
                    options: updatedOptions,
                })
                setStepError(null)
            }
        },
        [selectedNodeIds, composeAndPaste]
    )

    const handleOptionChange = useCallback(
        (key: string, value: string | boolean, autoAdvance: boolean) => {
            if (!flow) return
            const updatedOptions = { ...flow.options, [key]: value }
            setFlow((prev) =>
                prev ? { ...prev, options: updatedOptions } : null
            )
            setStepError(null)

            if (autoAdvance) {
                advanceOrFinish(
                    { ...flow, options: updatedOptions },
                    updatedOptions
                )
            }
        },
        [flow, advanceOrFinish]
    )

    const handleNext = useCallback(() => {
        if (!flow) return
        advanceOrFinish(flow, flow.options)
    }, [flow, advanceOrFinish])

    const handleCameraAngleChange = useCallback(
        (azimuth: number, elevation: number) => {
            if (!flow) return
            setFlow((prev) =>
                prev
                    ? {
                          ...prev,
                          cameraAzimuth: azimuth,
                          cameraElevation: elevation,
                          options: {
                              ...prev.options,
                              cameraAngle: `${azimuth},${elevation}`,
                          },
                      }
                    : null
            )
        },
        [flow]
    )

    const handleCameraZoomChange = useCallback(
        (zoom: number) => {
            if (!flow) return
            setFlow((prev) =>
                prev
                    ? {
                          ...prev,
                          cameraZoom: zoom,
                          options: { ...prev.options, cameraZoom: String(zoom) },
                      }
                    : null
            )
        },
        [flow]
    )

    const handleBack = useCallback(() => {
        if (!flow) return
        if (flow.step === 0) {
            setFlow(null)
            setStepError(null)
        } else {
            setFlow((prev) =>
                prev ? { ...prev, step: prev.step - 1 } : null
            )
            setStepError(null)
        }
    }, [flow])

    // ── Shared Preview Area ──

    const previewArea = (
        <div className={styles.previewArea}>
            {Array.from({ length: slotCount }, (_, i) => {
                const imageUrl = selectedImages[i]
                const tag = slotLabels?.[i]
                const defaultLabel = selectedImages.length === 0
                    ? 'Select an image on the canvas'
                    : `Image ${i + 1}`
                return (
                    <PreviewSlot
                        key={i}
                        imageUrl={imageUrl}
                        label={slotLabels?.[i] ?? defaultLabel}
                        tag={imageUrl ? tag : undefined}
                    />
                )
            })}
        </div>
    )

    // ── Tile Grid View ──

    if (!flow) {
        return (
            <div className={styles.container}>
                {previewArea}
                {tileError && (
                    <div className={styles.validationMessage}>
                        <AlertCircle className={styles.validationIcon} />
                        {tileError}
                    </div>
                )}
                <div className={styles.tileGrid}>
                    {PRESETS.map((preset) => (
                        <button
                            key={preset.id}
                            className={styles.tile}
                            disabled={isDisabled}
                            data-error={
                                tileError &&
                                selectedNodeIds.length < preset.requiredImages.min
                                    ? 'true'
                                    : undefined
                            }
                            onClick={() => handleTileClick(preset)}
                        >
                            <div className={styles.tileThumbnail}>
                                {preset.placeholderImage ? (
                                    <img
                                        src={preset.placeholderImage}
                                        alt=""
                                        className={styles.tileThumbnailImage}
                                    />
                                ) : (
                                    <preset.icon className={styles.tileThumbnailIcon} />
                                )}
                            </div>
                            <div className={styles.tileBody}>
                                <span className={styles.tileLabel}>
                                    {preset.label}
                                </span>
                                <span className={styles.tileDescription}>
                                    {preset.description}
                                </span>
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        )
    }

    // ── Multi-Step Flow View ──

    const preset = PRESETS.find((p) => p.id === flow.presetId)
    if (!preset) return <div className={styles.container} />

    const currentStep = preset.steps[flow.step]
    const totalSteps = preset.steps.length
    const isLastStep = flow.step === totalSteps - 1
    const showFooter = currentStep.type === 'toggle' || currentStep.type === 'camera-orbit'

    return (
        <div className={styles.container}>
            {previewArea}
            <div className={styles.flowContainer}>
                <div className={styles.flowHeader}>
                    <button
                        className={styles.flowBackButton}
                        onClick={handleBack}
                        aria-label="Back"
                    >
                        <ChevronLeft size={18} />
                    </button>
                    <span className={styles.flowTitle}>{preset.label}</span>
                    <button
                        className={styles.flowNextButton}
                        onClick={handleNext}
                        aria-label="Next step"
                    >
                        <span>Step {flow.step + 1} of {totalSteps}</span>
                        <ChevronRight size={16} />
                    </button>
                </div>

                <div className={styles.flowBody}>
                    <div className={styles.flowStepTitle}>
                        {currentStep.title}
                    </div>

                    {currentStep.type === 'select' && currentStep.choices && (
                        currentStep.choices.some((c) => c.image) ? (
                            <div className={styles.imageOptionGrid}>
                                {currentStep.choices.map((choice) => (
                                    <button
                                        key={choice.value}
                                        className={styles.imageOptionCard}
                                        data-selected={
                                            flow.options[currentStep.key] ===
                                            choice.value
                                                ? 'true'
                                                : undefined
                                        }
                                        onClick={() =>
                                            handleOptionChange(
                                                currentStep.key,
                                                choice.value,
                                                true
                                            )
                                        }
                                    >
                                        {choice.image && (
                                            <img
                                                src={choice.image}
                                                alt={choice.label}
                                                className={styles.imageOptionImg}
                                            />
                                        )}
                                        <span className={styles.imageOptionLabel}>
                                            {choice.label}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className={styles.optionGrid}>
                                {currentStep.choices.map((choice) => (
                                    <button
                                        key={choice.value}
                                        className={styles.optionChip}
                                        data-selected={
                                            flow.options[currentStep.key] ===
                                            choice.value
                                                ? 'true'
                                                : undefined
                                        }
                                        onClick={() =>
                                            handleOptionChange(
                                                currentStep.key,
                                                choice.value,
                                                true
                                            )
                                        }
                                    >
                                        {choice.label}
                                    </button>
                                ))}
                            </div>
                        )
                    )}

                    {currentStep.type === 'toggle' && (
                        <div className={styles.toggleRow}>
                            <span className={styles.toggleLabel}>
                                {currentStep.title}
                            </span>
                            <button
                                className={styles.toggleButton}
                                role="switch"
                                aria-checked={
                                    !!flow.options[currentStep.key]
                                }
                                data-on={
                                    flow.options[currentStep.key]
                                        ? 'true'
                                        : undefined
                                }
                                onClick={() =>
                                    handleOptionChange(
                                        currentStep.key,
                                        !flow.options[currentStep.key],
                                        false
                                    )
                                }
                                aria-label={`Toggle ${currentStep.title}`}
                            >
                                <span className={styles.toggleKnob} />
                            </button>
                        </div>
                    )}

                    {currentStep.type === 'camera-orbit' && (
                        <CameraOrbitPicker
                            thumbnailUrl={selectedImages[0]}
                            azimuth={flow.cameraAzimuth ?? 0}
                            elevation={flow.cameraElevation ?? 0}
                            zoom={flow.cameraZoom ?? 5}
                            onAngleChange={handleCameraAngleChange}
                            onZoomChange={handleCameraZoomChange}
                        />
                    )}

                    {stepError && (
                        <div className={styles.validationMessage}>
                            <AlertCircle className={styles.validationIcon} />
                            {stepError}
                        </div>
                    )}
                </div>

                {showFooter && (
                    <div className={styles.flowFooter}>
                        <button
                            className={styles.flowAction}
                            onClick={handleNext}
                        >
                            {currentStep.type === 'camera-orbit'
                                ? 'Generate'
                                : isLastStep ? 'Apply' : 'Next'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}
